import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';
import SEO from '../../components/SEO';
import Slider from 'react-slick';
import RatingDisplay from '../../components/RatingDisplay';
import CertificationsManager from '../../components/CertificationsManager';
import { useRating } from '../../hooks/useRating';
import { useUser } from '../../context/UserContext';
import userDefault from '../../assets/img/user.svg';
import { ProfilePageSkeleton } from '../../components/loading-states';

export default function AgentPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const [asesor, setAsesor] = useState<any>(null);
  const [inmobiliarias, setInmobiliarias] = useState<any[]>([]);
  const [reseñas, setReseñas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ratingData, setRatingData] = useState<any>(null);
  const { calculateUserRating } = useRating();

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) {
          setError('No se proporcionó un ID de asesor válido');
          setLoading(false);
          return;
        }

        const docRef = doc(db, 'asesor', id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setError('El asesor no fue encontrado');
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        setAsesor({ id: docSnap.id, ...data });

        // Cargar inmobiliarias usando inmobiliaria_doc_ids (array)
        if (data.inmobiliaria_doc_ids && Array.isArray(data.inmobiliaria_doc_ids)) {
          const inmobiliariaPromises = data.inmobiliaria_doc_ids.map(async (inmId: string) => {
            const inmDocRef = doc(db, 'inmobiliaria', inmId);
            const inmSnap = await getDoc(inmDocRef);
            if (inmSnap.exists()) {
              return { id: inmSnap.id, ...inmSnap.data() };
            }
            return null;
          });
          const inmosData = await Promise.all(inmobiliariaPromises);
          setInmobiliarias(inmosData.filter(Boolean));
        }

        // Cargar reseñas y calcular rating
        if (data.asesor_id) {
          try {
            const reseñasQuery = query(
              collection(db, 'calificacion'),
              where('perfil_id', '==', data.asesor_id),
              where('estatus', '==', 'aprobada')
            );
            const reseñasSnap = await getDocs(reseñasQuery);
            setReseñas(reseñasSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const ratingPromise = calculateUserRating(data.asesor_id, 'asesor');
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), 5000)
            );

            const rating = await Promise.race([ratingPromise, timeoutPromise]);
            setRatingData(rating);
          } catch (error) {
            console.error('Error calculando rating:', error);
            setRatingData({
              calificacionFinal: data.calificacion || 0,
              breakdown: null,
              totalReviews: 0
            });
          }
        } else {
          setRatingData({
            calificacionFinal: data.calificacion || 0,
            breakdown: null,
            totalReviews: 0
          });
        }

        setLoading(false);
      } catch (err) {
        console.error('Error al cargar datos del asesor:', err);
        setError('Error al cargar los datos del asesor');
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <>
        {user ? <UserNav /> : <Navbar transparent={false} />}
        <ProfilePageSkeleton color="primary" />
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  if (error || !asesor) {
    return (
      <>
        {user ? <UserNav /> : <Navbar transparent={false} />}
        <div className="container py-5">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error || 'Asesor no encontrado'}
          </div>
          <Link to="/agents" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i>
            Volver al listado
          </Link>
        </div>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  const agentName = asesor?.nombre || 'Asesor Inmobiliario';
  const agentTitle = 'Asesor Certificado AMPI';
  const agentImage = asesor?.foto || 'https://ampirivieranayarit.com/wp-content/uploads/2025/03/mob-slide-1.jpg';
  const agentLocation = 'Riviera Nayarit';

  const agentSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": agentName,
    "jobTitle": agentTitle,
    "description": `${agentName} - ${agentTitle} certificado por AMPI Riviera Nayarit`,
    "url": `https://ampirivieranayarit.com/modulob/agent-page/${id || ''}`,
    "image": agentImage,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": agentLocation,
      "addressCountry": "MX"
    },
    "memberOf": {
      "@type": "Organization",
      "name": "AMPI Riviera Nayarit",
      "url": "https://ampirivieranayarit.com"
    }
  };

  // Generar link de WhatsApp
  const whatsappNumber = asesor.telefono
    ? asesor.telefono.replace(/[\s\-\(\)]/g, '').replace(/^\+/, '')
    : null;
  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hola ${asesor.nombre}, me gustaría obtener más información.`)}`
    : null;

  return (
    <>
      <SEO
        title={`${agentName} - ${agentTitle}`}
        description={`Perfil de ${agentName}, ${agentTitle} certificado en ${agentLocation}. Contacta con asesores verificados por AMPI Riviera Nayarit.`}
        path={`/modulob/agent-page/${id || ''}`}
        image={agentImage}
        type="profile"
        schema={agentSchema}
      />
      {user ? <UserNav /> : <Navbar transparent={false} />}

      <Breadcrumb
        items={[
          { label: 'Inicio', path: '/' },
          { label: 'Asesores', path: '/agents' },
          { label: asesor.nombre || 'Asesor', path: '' }
        ]}
      />

      {/* Hero del Asesor */}
      <section className="bg-primary py-4">
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-center text-center text-md-start gap-4">
            <img
              src={asesor.foto || userDefault}
              alt={`Foto de ${asesor.nombre}`}
              className="rounded-circle border border-4 border-white shadow"
              width="150"
              height="150"
              style={{ objectFit: 'cover', flexShrink: 0 }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = userDefault;
              }}
            />
            <div>
              <h2 className="text-white mb-1">{asesor.nombre}</h2>
              <p className="text-white-50 mb-2">
                <i className="fas fa-certificate me-2"></i>
                Asesor Certificado AMPI
              </p>
              {asesor.años_experiencia > 0 && (
                <span className="badge bg-light text-primary me-2">
                  <i className="fas fa-briefcase me-1"></i>
                  {asesor.años_experiencia} años de experiencia
                </span>
              )}
              {inmobiliarias.length > 0 && inmobiliarias.map((inm, idx) => (
                <Link
                  key={idx}
                  to={`/agency-page/${inm.id}`}
                  className="badge bg-light text-primary text-decoration-none me-2"
                >
                  <i className="fas fa-building me-1"></i>
                  {inm.nombre}
                </Link>
              ))}

              {/* Redes Sociales */}
              {(asesor.redes?.facebook || asesor.redes?.instagram) && (
                <div className="d-flex gap-2 mt-3 justify-content-center justify-content-md-start">
                  {asesor.redes?.facebook && (
                    <a
                      href={asesor.redes.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-light"
                    >
                      <i className="fab fa-facebook text-primary"></i>
                    </a>
                  )}
                  {asesor.redes?.instagram && (
                    <a
                      href={asesor.redes.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-light"
                    >
                      <i className="fab fa-instagram text-danger"></i>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="gray-simple">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-12">
              {/* Biografía */}
              {asesor.biografia && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fas fa-info-circle text-primary me-2"></i>
                      Acerca de mí
                    </h5>
                    <p className="text-muted mb-0">{asesor.biografia}</p>
                  </div>
                </div>
              )}

              {/* Calificación Profesional */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4">
                  <h5 className="mb-3">
                    <i className="fas fa-star text-warning me-2"></i>
                    {t('agents.professionalRating')}
                  </h5>
                  {ratingData ? (
                    <RatingDisplay
                      score={ratingData.calificacionFinal}
                      showBreakdown={false}
                      size="lg"
                      className="mb-3"
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center py-4">
                      <div className="spinner-border text-warning me-3" role="status">
                        <span className="visually-hidden">{t('agents.calculating')}</span>
                      </div>
                      <span className="text-muted">{t('agents.calculating')}</span>
                    </div>
                  )}

                  {reseñas.length > 0 && (
                    <div className="alert alert-info mb-0">
                      <i className="fas fa-comments me-2"></i>
                      {t('agents.basedOn')} <strong>{reseñas.length}</strong> {t('agents.reviews')}
                    </div>
                  )}
                </div>
              </div>

              {/* Reseñas */}
              {reseñas.length > 0 && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fas fa-comments text-primary me-2"></i>
                      Reseñas
                    </h5>
                    <Slider {...sliderSettings}>
                      {reseñas.map((r, index) => (
                        <div key={index} className="p-2">
                          <div className="card p-4 text-center shadow-sm border">
                            <p className="mb-3 fst-italic">"{r.reseña}"</p>
                            <div className="mb-0">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <i key={star} className={`fas fa-star ${star <= r.estrellas ? 'text-warning' : 'text-muted'}`} />
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Slider>
                  </div>
                </div>
              )}

              {/* Certificaciones */}
              {asesor?.certificaciones && asesor.certificaciones.length > 0 && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fa-solid fa-certificate text-primary me-2"></i>
                      Certificaciones Profesionales
                    </h5>
                    <CertificationsManager
                      userId={id || ''}
                      userType="asesor"
                      certificaciones={asesor.certificaciones}
                      readOnly={true}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 col-md-12">
              {/* Contacto */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4">
                  <h5 className="mb-3">
                    <i className="fas fa-address-book text-primary me-2"></i>
                    Contacto
                  </h5>

                  {/* Teléfono */}
                  {asesor.telefono && (
                    <div className="d-flex align-items-center mb-3">
                      <i className="fas fa-phone text-primary me-3"></i>
                      <div>
                        <small className="text-muted d-block">Teléfono</small>
                        <a href={`tel:${asesor.telefono}`} className="fw-bold text-decoration-none">
                          {asesor.telefono}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  {asesor.correo && (
                    <div className="d-flex align-items-center mb-4">
                      <i className="fas fa-envelope text-primary me-3"></i>
                      <div className="text-truncate">
                        <small className="text-muted d-block">Email</small>
                        <a href={`mailto:${asesor.correo}`} className="fw-bold text-decoration-none">
                          {asesor.correo}
                        </a>
                      </div>
                    </div>
                  )}

                  <hr />

                  {/* Botones de acción */}
                  <div className="d-grid gap-2">
                    {whatsappUrl && (
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-success"
                      >
                        <i className="fab fa-whatsapp me-2"></i>
                        WhatsApp
                      </a>
                    )}
                    {asesor.correo && (
                      <a href={`mailto:${asesor.correo}`} className="btn btn-primary">
                        <i className="fas fa-envelope me-2"></i>
                        Enviar Email
                      </a>
                    )}
                    {asesor.telefono && (
                      <a href={`tel:${asesor.telefono}`} className="btn btn-outline-primary">
                        <i className="fas fa-phone me-2"></i>
                        Llamar
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Inmobiliaria(s) */}
              {inmobiliarias.length > 0 && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fas fa-building text-primary me-2"></i>
                      {inmobiliarias.length > 1 ? 'Inmobiliarias' : 'Inmobiliaria'}
                    </h5>
                    {inmobiliarias.map((inm, idx) => (
                      <div key={idx} className={`d-flex align-items-center ${idx < inmobiliarias.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}`}>
                        {inm.logo ? (
                          <img
                            src={inm.logo}
                            alt={inm.nombre}
                            className="rounded me-3"
                            width="50"
                            height="50"
                            style={{ objectFit: 'contain' }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div
                            className="bg-primary text-white rounded d-flex align-items-center justify-content-center me-3"
                            style={{ width: '50px', height: '50px', flexShrink: 0 }}
                          >
                            <i className="fas fa-building"></i>
                          </div>
                        )}
                        <div className="flex-grow-1">
                          <Link to={`/agency-page/${inm.id}`} className="fw-bold text-decoration-none d-block">
                            {inm.nombre}
                          </Link>
                          {inm.telefono && (
                            <small className="text-muted">{inm.telefono}</small>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
